/**
 * Enum-list picker: toggleable chips for a fixed set of values
 * (deviceType, connectionType, proxyTypes, ...).
 */

import type { FC } from 'react';

import { cn } from '@/lib/utils';

export interface EnumListPickerProps {
  options: readonly string[];
  values: readonly string[];
  onChange: (next: readonly string[]) => void;
  ariaLabel: string;
  formatOption?: (option: string) => string;
}

function defaultFormat(option: string): string {
  return option
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const EnumListPicker: FC<EnumListPickerProps> = ({
  options,
  values,
  onChange,
  ariaLabel,
  formatOption = defaultFormat,
}) => {
  const valueSet = new Set(values);
  const toggle = (option: string) => {
    if (valueSet.has(option)) {
      onChange(values.filter((v) => v !== option));
    } else {
      onChange([...values, option]);
    }
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex flex-wrap gap-1"
    >
      {options.map((opt) => {
        const active = valueSet.has(opt);
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={active}
            onClick={() => toggle(opt)}
            className={cn(
              'inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full border px-1.5 text-[11px] transition-colors',
              active
                ? 'border-primary/40 bg-primary/10 font-medium text-foreground'
                : 'border-border/60 bg-transparent text-muted-foreground hover:border-border hover:bg-muted/30 hover:text-foreground',
            )}
          >
            {formatOption(opt)}
          </button>
        );
      })}
    </div>
  );
};

export default EnumListPicker;
